'use client';

import React, { forwardRef } from 'react';
import { cx } from 'class-variance-authority';
import { variants } from './variants';
import { iconPackages } from './consts';
import { IconProps, IconSizesTypes, IconVariantsTypes, IconPackageName } from './types';

const variantResolvers: Record<
	IconPackageName,
	(args: { size: IconSizesTypes; variant?: IconVariantsTypes }) => string
> = {
	default: ({ size, variant }) => variants({ size, variant }),
	multicolor: ({ size }) => variants({ size }),
	size32: ({ size, variant }) => variants({ size: size ?? 'xxl', variant }),
};

const Icon = forwardRef<HTMLDivElement, IconProps>(
	({ iconPackage = 'default', name, variant, size, onClick, className }, ref) => {
		const packageIcons = iconPackages[iconPackage];

		if (!packageIcons) {
			console.error(`❌ Icon package "${iconPackage}" not found. Available packages:`, Object.keys(iconPackages));
			return null;
		}

		const SvgContent = packageIcons[name];

		if (!SvgContent) {
			console.error(`❌ Icon "${name}" not found in package "${iconPackage}".`);
			return null;
		}

		const resolvedSize: IconSizesTypes = size ?? (iconPackage === 'size32' ? 'xxl' : 'md');
		const variantClasses = variantResolvers[iconPackage]({ size: resolvedSize, variant });

		return (
			<div
				ref={ref}
				onClick={onClick}
				className={`flex items-center justify-center [&_svg]:h-full [&_svg]:w-full ${cx(variantClasses, className)}`}>
				<SvgContent fill='currentColor' />
			</div>
		);
	},
);

Icon.displayName = 'Icon';

export { Icon };
export type { IconProps };
